/**
 * Wraps a Formie form with AJAX UX: spinner, success display with countdown,
 * validation scroll, submit-once guard, and optional modal lifecycle
 * (populate hidden fields on open).
 *
 * Calls window.Formie.initForm() imperatively — Formie's page-load scan uses
 * querySelectorAll, which skips <template> content, so forms inside
 * <template x-teleport> would otherwise never bind.
 */
export default function FormieEnhance(rawSettings = {}) {
  const settings = {
    successMode: 'overlay', // 'overlay' | 'closeModal'
    successMessage: "Thank you — we'll be in touch shortly.",
    closeDelay: 4000,
    modalId: null,
    populateOnOpen: {},
    ...rawSettings,
  };

  let countdownTimer = null;

  return {
    settings,
    submitting: false,
    submitted: false,
    message: settings.successMessage,
    countdown: 0,

    init() {
      this.bindFormie();
      if (!settings.modalId) return;

      window.addEventListener('modal:open', (e) => {
        if (e.detail?.id === settings.modalId) {
          this.populate(e.detail.detail || {});
        }
      });
      window.addEventListener('modal:close', () => this.reset());
    },

    get $form() {
      return this.$el.querySelector('form[data-fui-form]');
    },

    bindFormie() {
      const $form = this.$form;
      const init = () => {
        if (!$form || $form.dataset.fuiInitialized) return;
        window.Formie.initForm($form);
        $form.dataset.fuiInitialized = '1';
      };
      window.Formie
        ? init()
        : document.addEventListener('onFormieLoaded', init, {
            once: true,
          });
    },

    populate(detail) {
      const $form = this.$form;
      if (!$form) return;
      this.reset();
      $form.reset();
      for (const [field, key] of Object.entries(settings.populateOnOpen)) {
        const $input = $form.querySelector(`[name="fields[${field}]"]`);
        if ($input && detail[key] !== undefined) {
          $input.value = detail[key];
          $input.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
    },

    reset() {
      this.stopCountdown();
      this.submitted = false;
      this.message = settings.successMessage;
      this.clearSubmitting();
    },

    clearSubmitting() {
      this.submitting = false;
      this.setSubmitDisabled(false);
    },

    setSubmitDisabled(disabled) {
      const $btn = this.$form?.querySelector('button[type="submit"]');
      if ($btn) $btn.disabled = disabled;
    },

    startCountdown() {
      this.stopCountdown();
      this.countdown = Math.max(1, Math.ceil(settings.closeDelay / 1000));
      const tick = () => {
        this.countdown -= 1;
        if (this.countdown <= 0) {
          countdownTimer = null;
          window.dispatchEvent(new CustomEvent('modal:close'));
        } else {
          countdownTimer = setTimeout(tick, 1000);
        }
      };
      countdownTimer = setTimeout(tick, 1000);
    },

    stopCountdown() {
      if (countdownTimer) clearTimeout(countdownTimer);
      countdownTimer = null;
      this.countdown = 0;
    },

    dismiss() {
      this.stopCountdown();
      window.dispatchEvent(new CustomEvent('modal:close'));
    },

    owns(e) {
      const target = e.detail?.form || e.target;
      return target && this.$el.contains(target);
    },

    onBefore(e) {
      if (!this.owns(e)) return;
      this.submitting = true;
      this.setSubmitDisabled(true);
    },

    onAfter(e) {
      if (!this.owns(e)) return;
      this.clearSubmitting();
      if (e.detail?.success === false) return;
      this.submitted = true;
      this.message = e.detail?.submitActionMessage || settings.successMessage;
      if (settings.successMode === 'closeModal') this.startCountdown();
    },

    onError(e) {
      if (this.owns(e)) this.clearSubmitting();
    },

    onValidate(e) {
      if (!this.owns(e)) return;
      this.clearSubmitting();
      const $invalid = this.$el.querySelector(
        '.fui-error input, .fui-error select, .fui-error textarea',
      );
      if ($invalid) {
        $invalid.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        $invalid.focus({ preventScroll: true });
      }
    },

    get formClass() {
      if (this.submitted) return 'opacity-25 pointer-events-none select-none';
      if (this.submitting) return 'opacity-40 pointer-events-none select-none';
      return 'opacity-100';
    },
  };
}
